import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Menu,
  MenuItem,
  Collapse,
  Paper,
  Grid,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useState, useMemo } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { GiHamburgerMenu } from "react-icons/gi";
import { GoTrash } from "react-icons/go";
import Stop from "./Stop";
import StopStatus from "src/components/apps/order-components/StopsStatus";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { deleteOrderStop } from "src/queries/query";
import { useParams } from "react-router";
import LegsTable from "./order-legs";
import { format, isYesterday, parseISO, isToday } from "date-fns";

export default function StopsComponent({
  values,
  setFieldValue,
  setTouched,
  detailsRef,
  commoditiesRef,
  customersAddresses = [],
  customers = [],
  accessorialsData = [],
  orderId,
  touched = {},
  errors = {},
  onStopsReorder,
  setStops,
  disabledView,
  type,
  onAddressSelect,
}) {
  const { uuid } = useParams();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedStop, setSelectedStop] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());

  const formatAddress = (address, type) => {
    // If address is falsy, return N/A
    if (!address) return "N/A";

    // If address is already a string (e.g., "Rome"), return it
    if (typeof address === "string") {
      return address.trim() || "N/A";
    }

    // If address is an object, build parts safely
    const parts = [
      address?.address,
      address?.address2,
      address?.city,
      address?.state,
      address?.country,
      address?.postal,
    ]
      // Filter out undefined, null, or empty strings
      .filter((part) => part && part.toString().trim());
    return parts.length > 0 ? parts.join(", ") : "N/A";
  };

  const handleMenuOpen = (event, stopId) => {
    setAnchorEl(event.currentTarget);
    setSelectedStop(stopId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedStop(null);
  };

  // Helper function to create a deep copy of an object
  const deepCopy = (obj) => {
    if (!obj) return undefined;
    return JSON.parse(JSON.stringify(obj));
  };

  const addStop = () => {
    const firstStop = deepCopy(values?.orderStops?.[0]) || {};
    const stopIndex = stops?.length || 0;

    const defaultAddress = {
      name: "",
      addresses: {
        address: "",
        address2: "",
        city: "",
        state: "",
        country: "",
        postal: "",
        latitude: "",
        longitude: "",
      },
    };

    // Create deep copies of the addresses to avoid reference issues
    const clonedConsignee =
      values?.orderType === "single_consignee"
        ? deepCopy(
            values?.consignee ||
              firstStop?.consignee ||
              firstStop?.consigneeAddress ||
              defaultAddress,
          )
        : deepCopy(defaultAddress);

    const clonedShipper =
      values?.orderType === "single_shipper"
        ? deepCopy(
            values?.shipper ||
              firstStop?.shipper ||
              firstStop?.shipperAddress ||
              defaultAddress,
          )
        : values?.orderType === "single_consignee"
          ? deepCopy(defaultAddress)
          : deepCopy(
              firstStop?.shipper || firstStop?.shipperAddress || defaultAddress,
            );

    // Fields to exclude from cloning
    const excludeKeys = new Set([
      "id",
      "stopNumber",
      "readyTimeStart",
      "readyTimeEnd",
      "deliveryTimeStart",
      "deliveryTimeEnd",
      "requestedTimeStart",
      "requestedTimeEnd",
      "availableTimeStart",
      "availableTimeEnd",
      "isAppointmentDelivery",
      "isAppointmentConfirmDelivery",
      "isAppointmentPickup",
      "isAppointmentConfirmPickup",
      "accessorialUuids",
      "commodities",
      "dims",
      // Ensure we never clone identifiers for new stops
      "uuid",
      "orderStopUuid",
      "_id",
    ]);

    // Conditional exclusions
    if (values?.orderType === "single_consignee") {
      excludeKeys.add("shipper");
      excludeKeys.add("shipperAddress");
    }
    if (values?.orderType === "single_shipper") {
      excludeKeys.add("consignee");
      excludeKeys.add("consigneeAddress");
    }
    // Clone remaining fields from firstStop
    const clonedFields = Object.keys(firstStop).reduce((acc, key) => {
      if (excludeKeys.has(key)) return acc;

      const val = firstStop[key];
      if (Array.isArray(val)) {
        acc[key] = [];
      } else if (typeof val === "object" && val !== null) {
        acc[key] = { ...val };
      } else {
        acc[key] = "";
      }

      return acc;
    }, {});

    // Build the new stop
    const newStop = {
      id: stopIndex + 1,
      stopNumber: stopIndex + 1,
      readyTimeStart: firstStop.readyTimeStart || "",
      readyTimeEnd: firstStop.readyTimeEnd || "",
      isAppointmentPickup: firstStop.isAppointmentPickup || false,
      isAppointmentConfirmPickup: firstStop.isAppointmentConfirmPickup || false,
      accessorialUuids: [],
      commodities: [],
      shipperAddress: deepCopy({}),
      consigneeAddress: deepCopy({}),
      shipper:
        values?.orderType === "single_shipper"
          ? deepCopy(clonedShipper)
          : deepCopy(defaultAddress),
      consignee:
        values?.orderType === "single_consignee"
          ? deepCopy(clonedConsignee)
          : deepCopy(defaultAddress),
      dims: {
        lf: "0 LF",
        lbs: "0 lbs",
      },
      ...clonedFields, // Merge dynamically cloned fields here
    };

    // Explicitly drop any lingering identifiers that might have slipped through
    delete newStop.uuid;
    delete newStop.orderStopUuid;
    delete newStop._id;

    if (newStop.isAppointmentPickup) {
      newStop.pickUpTimeStart = firstStop.pickUpTimeStart;
      newStop.pickUpTimeEnd = firstStop.pickUpTimeEnd;
    }

    // You can now push newStop to your stops array
    setFieldValue("orderStops", [...(values?.orderStops || []), newStop]);
  };

  const addStopBefore = () => {
    if (selectedStop === null) return;
    const newStop = {
      id: values?.orderStops?.length + 1,
      stopNumber: 1, // Will be updated after insertion
      shipper: {
        name: "",
        location: "",
        status: "NEW",
      },
      consignee: {
        name: "N/A",
        location: "N/A",
      },
      manifest: "N/A",
      dims: {
        lf: "0 LF",
        lbs: "lbs",
      },
    };
    const newStops = [...values.orderStops];
    const index = newStops.findIndex((stop) => stop.id === selectedStop);
    newStops.splice(index, 0, newStop);

    // Update stop numbers for all stops after insertion
    const updatedStops = newStops.map((stop, idx) => ({
      ...stop,
      stopNumber: idx + 1,
    }));

    setFieldValue("orderStops", updatedStops);
    // Automatically expand the newly created stop
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      newSet.add(index); // Add the index where the new stop was inserted
      return newSet;
    });
    handleMenuClose();
  };

  const addStopAfter = () => {
    if (selectedStop === null) return;
    const newStop = {
      id: values?.orderStops?.length + 1,
      stopNumber: 1, // Will be updated after insertion
      shipper: {
        name: "",
        location: "",
        status: "NEW",
      },
      consignee: {
        name: "N/A",
        location: "N/A",
      },
      manifest: "N/A",
      dims: {
        lf: "0 LF",
        lbs: "lbs",
      },
    };
    const newStops = [...values.orderStops];
    const index = newStops.findIndex((stop) => stop.id === selectedStop);
    newStops.splice(index + 1, 0, newStop);

    // Update stop numbers for all stops after insertion
    const updatedStops = newStops.map((stop, idx) => ({
      ...stop,
      stopNumber: idx + 1,
    }));

    setFieldValue("orderStops", updatedStops);
    // Automatically expand the newly created stop
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      newSet.add(index + 1); // Add the index where the new stop was inserted
      return newSet;
    });
    handleMenuClose();
  };

  const addEmptyStop = () => {
    const newStop = {
      id: values?.orderStops?.length + 1,
      stopNumber: (values?.orderStops?.length || 0) + 1,
      shipper: {
        name: "",
        location: "",
        status: "NEW",
      },
      consignee: {
        name: "",
        location: "",
      },
      manifest: "",
      dims: {
        lf: "0 LF",
        lbs: "lbs",
      },
    };
    const newStops = [...values.orderStops];
    const finalStops = [...newStops, newStop];
    setFieldValue("orderStops", finalStops);
    // Automatically expand the newly created stop
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      newSet.add(finalStops.length - 1); // Add the index of the new stop
      return newSet;
    });
    handleMenuClose();
  };

  const { mutate: deleteStop, isPending } = useMutation({
    mutationFn: (payload) => {
      return deleteOrderStop(payload);
    },
    onSuccess: () => {
      toast.success("Stop deleted successfully!");
    },
    onError: (error) => {
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          "Failed to delete Stop",
      );
    },
  });

  const removeStop = () => {
    if (selectedStop === null) return;

    // Find the stop object by id so we can check for uuid
    const stopToRemove = values?.orderStops?.find(
      (stop) => stop?.id === selectedStop,
    );

    const newStops = values?.orderStops?.filter(
      (stop) => stop?.id !== selectedStop,
    );

    const updatedStops = newStops.map((stop, idx) => ({
      ...stop,
      stopNumber: idx + 1,
    }));

    // Only call API to delete if the stop exists on server (has uuid)
    if (stopToRemove?.uuid) {
      deleteStop({ orderStopUuids: [stopToRemove.uuid], orderUuid: uuid });
    }

    setFieldValue("orderStops", updatedStops);
    handleMenuClose();
  };

  const stops = values?.orderStops;

  const duplicateStop = () => {
    if (selectedStop === null) return;
    const stopToDuplicate = values?.orderStops?.find(
      (stop) => stop?.id === selectedStop,
    );
    const duplicatedStop = {
      ...stopToDuplicate,
      id: values?.orderStops?.length + 1,
      stopNumber: 1, // Will be updated after insertion
    };
    // Remove identifiers so this is treated as a new local stop
    delete duplicatedStop.uuid;
    delete duplicatedStop.orderStopUuid;
    delete duplicatedStop._id;
    const newStops = [...values?.orderStops];
    const index = newStops?.findIndex((stop) => stop?.id === selectedStop);
    newStops.splice(index + 1, 0, duplicatedStop);

    // Update stop numbers for all stops after duplication
    const updatedStops = newStops.map((stop, idx) => ({
      ...stop,
      stopNumber: idx + 1,
    }));

    setFieldValue("orderStops", updatedStops);
    // Automatically expand the newly created stop
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      newSet.add(index + 1); // Add the index where the duplicated stop was inserted
      return newSet;
    });
    handleMenuClose();
  };

  // Function to loop over stops and process them
  const processStops = () => {
    if (!stops || stops.length === 0) {
      return;
    }
  };

  const handleRowToggle = (stopIndex) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(stopIndex)) {
        newSet.delete(stopIndex);
      } else {
        newSet.add(stopIndex);
      }
      return newSet;
    });
  };

  // Create sorted stops with mapping back to original indices
  const sortedStops = useMemo(() => {
    if (!stops) return [];

    return stops
      .map((stop, originalIndex) => ({ stop, originalIndex }))
      .sort((a, b) => {
        const aNum =
          a.stop.stopNumber !== undefined ? a.stop.stopNumber : a.originalIndex;
        const bNum =
          b.stop.stopNumber !== undefined ? b.stop.stopNumber : b.originalIndex;
        return aNum - bNum;
      });
  }, [stops]);

  // Updated handleDragEnd function that properly updates stopNumber
  const handleDragEnd = (result) => {
    // If disabledView is true, don't allow dragging
    if (disabledView) return;

    if (!result.destination) return;
    const { source, destination } = result;

    // If the item was dropped in the same position, do nothing
    if (source.index === destination.index) return;

    // Work with the current stops array
    const currentStops = [...stops];

    // Find the stop being moved based on the sorted index
    const stopToMove = sortedStops[source.index].stop;
    const originalIndex = currentStops.findIndex(
      (stop) => stop.id === stopToMove.id,
    );

    // Remove the stop from its current position
    const [movedStop] = currentStops.splice(originalIndex, 1);

    // Calculate the new position in the original array
    let newIndex;
    if (destination.index === 0) {
      newIndex = 0;
    } else if (destination.index >= sortedStops.length - 1) {
      newIndex = currentStops.length;
    } else {
      // Find where to insert based on the destination in sorted order
      const stopAtDestination = sortedStops[destination.index].stop;
      newIndex = currentStops.findIndex(
        (stop) => stop.id === stopAtDestination.id,
      );
      if (newIndex === -1) newIndex = destination.index;
    }

    // Insert the stop at the new position
    currentStops.splice(newIndex, 0, movedStop);

    // Update stopNumber for all stops to reflect their new order
    const reorderedStops = currentStops.map((stop, index) => ({
      ...stop,
      stopNumber: index + 1,
    }));

    // Update the stops state
    setStops(reorderedStops);
    setFieldValue("orderStops", reorderedStops);

    // Call the callback function if provided
    if (onStopsReorder) {
      onStopsReorder(reorderedStops, source.index, destination.index);
    }
  };
  const formatDate = (dateString) => {
    if (!dateString) return "";

    const date = parseISO(dateString);
    const timeStr = format(date, "HH:mm");

    if (isToday(date)) {
      return `Today @ ${timeStr}`;
    } else if (isYesterday(date)) {
      return `Yesterday @ ${timeStr}`;
    } else {
      return format(date, "MMM d @ HH:mm");
    }
  };
  const selectedQuote = values?.orderStops
    ?.flatMap((stop) => stop?.quotes)
    ?.find((quote) => quote?.isSelected);

  return (
    <Box sx={{ py: 2, px: 2, pt: 0 }} ref={detailsRef}>
      <Typography variant="h6" component="h2" gutterBottom>
        Stops ({stops ? stops.length : 0})
      </Typography>
      <TableContainer sx={{ mt: 2, borderRadius: 0 }}>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Table
            size="small"
            sx={{ borderSpacing: "0 8px", borderCollapse: "separate" }}
          >
            <TableHead sx={{ bgcolor: "#f5f5f5" }}>
              <TableRow>
                <TableCell width="1%" sx={{ borderBottom: "none" }}></TableCell>
                <TableCell width="8%" sx={{ borderBottom: "none" }}>
                  Stop #
                </TableCell>
                <TableCell width="20%" sx={{ borderBottom: "none" }}>
                  Shipper <span style={{ color: "red" }}>*</span>
                </TableCell>
                <TableCell width="20%" sx={{ borderBottom: "none" }}>
                  Consignee <span style={{ color: "red" }}>*</span>
                </TableCell>
                <TableCell width="10%" sx={{ borderBottom: "none" }}>
                  Manifest
                </TableCell>
                <TableCell width="10%" sx={{ borderBottom: "none" }}>
                  Dims <span style={{ color: "red" }}>*</span>
                </TableCell>
                <TableCell width="10%" sx={{ borderBottom: "none" }}>
                  Status
                </TableCell>
                <TableCell width="1%" sx={{ borderBottom: "none" }}></TableCell>
              </TableRow>
            </TableHead>
            <Droppable droppableId="stops-table">
              {(provided) => (
                <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                  {sortedStops.length > 0 ? (
                    sortedStops.map(({ stop, originalIndex }, sortedIndex) => (
                      <Draggable
                        key={stop.id || originalIndex}
                        draggableId={String(stop.id || originalIndex)}
                        index={sortedIndex}
                      >
                        {(provided, snapshot) => (
                          <>
                            <TableRow
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              sx={{
                                backgroundColor: snapshot.isDragging
                                  ? "rgba(0, 102, 255, 0.05)"
                                  : expandedRows.has(originalIndex)
                                    ? "rgba(0, 102, 255, 0.05)"
                                    : "white",
                                "&:hover": {
                                  backgroundColor: snapshot.isDragging
                                    ? "rgba(0, 102, 255, 0.05)"
                                    : "rgba(0, 102, 255, 0.05)",
                                },
                                cursor: "pointer",
                                border: "1px solid #000",
                                borderRadius: "10px",
                                mx: 1,
                                mb: 1,
                              }}
                              onClick={() => handleRowToggle(originalIndex)}
                            >
                              {!disabledView ? (
                                <TableCell sx={{ py: 1 }}>
                                  <Box
                                    {...(disabledView
                                      ? {}
                                      : provided.dragHandleProps)}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      cursor: disabledView ? "default" : "grab",
                                      opacity: disabledView ? 0.5 : 1,
                                      "&:active": {
                                        cursor: disabledView
                                          ? "default"
                                          : "grabbing",
                                      },
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <GiHamburgerMenu className="text-gray-500 text-base" />
                                  </Box>
                                </TableCell>
                              ) : (
                                <TableCell sx={{ py: 1 }}></TableCell>
                              )}
                              <TableCell sx={{ py: 1 }}>
                                <Typography variant="body2">
                                  {stop.stopNumber}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 1 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: "bold" }}
                                >
                                  {stop.shipper?.name ||
                                    stop.shipperAddress?.company ||
                                    "N/A"}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {formatAddress(
                                    stop.shipper?.addresses ||
                                      stop.shipperAddress?.addresses ||
                                      stop.shipperAddress?.address,
                                  ) || "N/A"}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 1 }}>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: "bold" }}
                                >
                                  {stop.consignee?.name ||
                                    stop.consigneeAddress?.company ||
                                    "N/A"}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {formatAddress(
                                    stop.consignee?.addresses ||
                                      stop.consigneeAddress?.addresses ||
                                      stop.consigneeAddress?.address,
                                  ) || "N/A"}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 1 }}>
                                {stop.manifest || "N/A"}
                              </TableCell>
                              <TableCell sx={{ py: 1 }}>
                                <Typography variant="body2">
                                  {stop.commodities
                                    ?.map((item) => item.lf)
                                    .reduce((a, b) => a + b, 0) + " LF" ||
                                    "0 LF"}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {stop.commodities
                                    ?.map((item) => item.weight)
                                    .reduce((a, b) => a + b, 0) + " lbs" ||
                                    "0 lbs"}
                                </Typography>
                              </TableCell>
                              <TableCell sx={{ py: 1 }}>
                                <StopStatus status={stop?.status || "NEW"} />
                              </TableCell>
                              {!disabledView ? (
                                <TableCell sx={{ py: 1 }}>
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMenuOpen(e, stop.id);
                                    }}
                                    aria-controls={`stop-menu-${stop.id}`}
                                    aria-haspopup="true"
                                  >
                                    <MoreVertIcon />
                                  </IconButton>
                                </TableCell>
                              ) : (
                                <TableCell sx={{ py: 1 }}></TableCell>
                              )}
                            </TableRow>
                            <TableRow>
                              <TableCell
                                colSpan={9}
                                sx={{ p: 0, border: 0, borderRadius: 0 }}
                              >
                                <Collapse
                                  in={expandedRows.has(originalIndex)}
                                  timeout="auto"
                                  unmountOnExit
                                >
                                  <Paper
                                    sx={{
                                      borderBottom: "1px solid #e0e0e0",
                                      borderRadius: 2,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        width: "100%",
                                        justifyContent: "space-between",
                                        border: 0,
                                        borderRadius: "10px 10px 0 0",
                                      }}
                                    >
                                      {disabledView ? null : (
                                        <Box
                                          sx={{
                                            px: 2,
                                            py: 1,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            width: "100%",
                                            bgcolor: "#f5f5f5",
                                          }}
                                        >
                                          <Typography
                                            variant="subtitle2"
                                            sx={{ mr: 1, fontWeight: "bold" }}
                                          >
                                            Stop{" "}
                                            {stop.stopNumber ||
                                              originalIndex + 1}
                                          </Typography>
                                          <IconButton
                                            size="small"
                                            color="error"
                                            aria-label="Delete stop"
                                            onClick={() => {
                                              // Remove stop at original index
                                              const updatedStops = [...stops];
                                              updatedStops.splice(
                                                originalIndex,
                                                1,
                                              );

                                              // Update stop numbers after removal
                                              const reorderedStops =
                                                updatedStops.map(
                                                  (stop, idx) => ({
                                                    ...stop,
                                                    stopNumber: idx + 1,
                                                  }),
                                                );
                                              // Only call API if stop has a persisted uuid
                                              if (stop?.uuid) {
                                                deleteStop({
                                                  orderStopUuids: [stop.uuid],
                                                  orderUuid: uuid,
                                                });
                                              }
                                              setStops(reorderedStops);
                                              // Optionally, also update formik field if needed
                                              if (
                                                typeof setFieldValue ===
                                                "function"
                                              ) {
                                                setFieldValue(
                                                  "orderStops",
                                                  reorderedStops,
                                                );
                                              }
                                            }}
                                            sx={{ ml: 1 }}
                                          >
                                            {/* <span className="material-icons"> */}
                                            <GoTrash />
                                            {/* </span> */}
                                          </IconButton>
                                        </Box>
                                      )}
                                    </Box>
                                    <Stop
                                      values={values}
                                      setFieldValue={setFieldValue}
                                      setTouched={setTouched}
                                      detailsRef={detailsRef}
                                      commoditiesRef={commoditiesRef}
                                      shipperCustomers={customersAddresses}
                                      consigneeCustomers={customersAddresses}
                                      billingCustomers={customers}
                                      accessorialsData={accessorialsData}
                                      orderId={orderId}
                                      touched={touched}
                                      errors={errors}
                                      stopData={stop}
                                      stopIndex={originalIndex}
                                      stops={stops}
                                      disabledView={disabledView}
                                      type={type}
                                      onAddressSelect={onAddressSelect}
                                    />
                                    {/* {selectedQuote && ( */}
                                    <Box sx={{ mb: 3 }}>
                                      <LegsTable
                                        order={stop}
                                        selectedQuote={selectedQuote}
                                        formatDate={formatDate}
                                        isInvoicePaid={
                                          values?.status?.toLowerCase() ===
                                          "invoicepaid"
                                        }
                                      />
                                    </Box>
                                    {/* )} */}

                                    {/* Miles and Time to Next Stop - Only show if not the last stop */}
                                    {/* {originalIndex < stops.length - 1 && (
                                      <Box sx={{ px: 2, pb: 2 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                                          To Next Stop
                                        </Typography>
                                        <Grid container spacing={2}>
                                          <Grid item xs={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <Typography variant="body2" color="text.secondary">
                                                Miles:
                                              </Typography>
                                              <Typography variant="body2" fontWeight="medium">
                                                {stop.milesToNextStop || '—'}
                                              </Typography>
                                            </Box>
                                          </Grid>
                                          <Grid item xs={6}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <Typography variant="body2" color="text.secondary">
                                                Time:
                                              </Typography>
                                              <Typography variant="body2" fontWeight="medium">
                                                {stop.timeToNextStop || '—'}
                                              </Typography>
                                            </Box>
                                          </Grid>
                                        </Grid>
                                      </Box>
                                    )} */}
                                  </Paper>
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </>
                        )}
                      </Draggable>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={9} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No stops available
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </Table>
        </DragDropContext>
      </TableContainer>
      {!disabledView && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            flexDirection: "column",
            mt: 2,
            gap: 2,
          }}
        >
          <div className="w-full flex justify-center items-center gap-2">
            <Button
              variant="contained"
              color="primary"
              onClick={addStop}
              sx={{ textTransform: "none", flexGrow: 0, width: "150px" }}
            >
              Add stop
            </Button>
          </div>
        </Box>
      )}
      <Menu
        id={`stop-menu-${selectedStop}`}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            border: "1px solid #1e55bf",
            borderTop: "4px solid #1e55bf",
            px: 2,
          },
        }}
      >
        <MenuItem onClick={addStopAfter}>Add stop after</MenuItem>
        <MenuItem onClick={addStopBefore}>Add stop before</MenuItem>
        <MenuItem onClick={addEmptyStop}>Add an empty stop</MenuItem>
        <MenuItem onClick={removeStop}>Remove stop</MenuItem>
        <MenuItem onClick={duplicateStop}>Duplicate stop</MenuItem>
      </Menu>
    </Box>
  );
}
